import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Alert,
  Space,
  Tag,
  Divider,
  message,
  AutoComplete,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  DollarOutlined,
  GiftOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { PayrollEmployee } from "../../../types/hr";
import { formatCurrency } from "../utils";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface QuickAddVariableModalProps {
  visible: boolean;
  employee: PayrollEmployee | null;
  payrollItems: any[];
  initialVariableType?: string;
  initialPayrollItemId?: number;
  periodName?: string;
  periodEndDate?: string;
  historicalProfileRequired?: boolean;
  onCancel: () => void;
  onSave: (variableData: VariableData) => Promise<void>;
}

interface VariableData {
  employee_id: number;
  variable_type: string;
  value: number;
  payroll_item_id?: number;
  description?: string;
  historical_employee_context?: {
    version: 1;
    hire_date: string;
    professional_category: "cadre" | "non_cadre";
    dependents_count: number;
  };
}

interface PayrollItemSuggestion {
  id: number;
  code: string;
  name: string;
  type: "gain" | "deduction" | "info";
  category: string;
  calculation_type: "fixed" | "percentage" | "formula";
  base_amount: number;
  percentage: number;
}

const QuickAddVariableModal: React.FC<QuickAddVariableModalProps> = ({
  visible,
  employee,
  payrollItems,
  initialVariableType,
  initialPayrollItemId,
  periodName,
  periodEndDate,
  historicalProfileRequired = false,
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [variableType, setVariableType] = useState<string>("");
  const [selectedPayrollItem, setSelectedPayrollItem] =
    useState<PayrollItemSuggestion | null>(null);
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0);

  useEffect(() => {
    if (visible && employee) {
      form.resetFields();
      setVariableType(initialVariableType || "");
      setSelectedPayrollItem(payrollItems.find(item => item.id === initialPayrollItemId) || null);
      form.setFieldsValue({ variable_type: initialVariableType, payroll_item_id: initialPayrollItemId });
      setCalculatedAmount(0);
    }
  }, [visible, employee, initialVariableType, initialPayrollItemId, historicalProfileRequired]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const salaryItem = variableType === 'salaire_base' ? payrollItems.find(item => item.code === 'SAL_BASE') : null;
      if (variableType === 'salaire_base' && !salaryItem) throw new Error('La rubrique SAL_BASE doit être disponible pour enregistrer le salaire de la période.');

      const variableData: VariableData = {
        employee_id: employee!.employee_id ?? employee!.id,
        // A named SAL_BASE value uses the existing custom variable contract,
        // which is also supported by databases retaining the historical ENUM.
        variable_type: salaryItem ? 'custom' : variableType,
        value: historicalProfileRequired ? values.dependents_count : values.amount ?? values.days ?? values.hours ?? calculatedAmount,
        payroll_item_id: historicalProfileRequired ? undefined : salaryItem?.id ?? selectedPayrollItem?.id,
        description: values.description,
        ...(historicalProfileRequired ? { historical_employee_context: {
          version: 1 as const,
          hire_date: values.hire_date.format('YYYY-MM-DD'),
          professional_category: values.professional_category,
          dependents_count: values.dependents_count,
        } } : {}),
      };

      await onSave(variableData);
      message.success("Variable ajoutée avec succès");
      onCancel();
      form.resetFields();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Erreur lors de l'ajout de la variable");
    } finally {
      setLoading(false);
    }
  };

  const handlePayrollItemSelect = (itemId: number) => {
    const item = payrollItems.find((pi) => pi.id === itemId);
    if (!item) { setSelectedPayrollItem(null); return; }
    if (item) {
      setSelectedPayrollItem(item);

      // Calculer le montant automatiquement selon le type
      if (initialVariableType) return; // A historical amount must be entered, not filled from today's catalog.
      if (item.calculation_type === "fixed") {
        setCalculatedAmount(item.base_amount);
        form.setFieldValue("amount", item.base_amount);
      } else if (item.calculation_type === "percentage" && employee) {
        const calculated = employee.base_salary * (item.percentage / 100);
        setCalculatedAmount(calculated);
        form.setFieldValue("amount", calculated);
      }
    }
  };

  const getVariableTypeOptions = () => [
    ...(historicalProfileRequired ? [{ label: "Profil de la période", options: [
      { value: "dependents", label: "Profil applicable à cette période", icon: <ClockCircleOutlined /> },
    ] }] : []),
    {
      label: "Présences",
      options: [
        {
          value: "attendance_days",
          label: "Jours travaillés",
          icon: <ClockCircleOutlined />,
        },
        {
          value: "vacation_days",
          label: "Jours de congé",
          icon: <ClockCircleOutlined />,
        },
        {
          value: "sick_days",
          label: "Arrêts maladie",
          icon: <ClockCircleOutlined />,
        },
        {
          value: "unpaid_leave",
          label: "Absences non payées",
          icon: <ClockCircleOutlined />,
        },
      ],
    },
    {
      label: "Heures supplémentaires",
      options: [
        {
          value: "overtime_hours",
          label: "Heures supplémentaires",
          icon: <ClockCircleOutlined />,
        },
        {
          value: "overtime_rate",
          label: "Taux heures sup",
          icon: <ClockCircleOutlined />,
        },
      ],
    },
    {
      label: "Gains",
      options: [
        { value: "salaire_base", label: "Salaire de base de la période", icon: <DollarOutlined /> },
        { value: "bonus", label: "Prime", icon: <GiftOutlined /> },
        { value: "allowance", label: "Indemnité", icon: <GiftOutlined /> },
        { value: "commission", label: "Commission", icon: <GiftOutlined /> },
      ],
    },
    {
      label: "Déductions",
      options: [
        { value: "advance", label: "Avance", icon: <MinusCircleOutlined /> },
        {
          value: "loan_deduction",
          label: "Retenue prêt",
          icon: <MinusCircleOutlined />,
        },
        {
          value: "other_deduction",
          label: "Autre déduction",
          icon: <MinusCircleOutlined />,
        },
      ],
    },
  ];

  const getInputField = () => {
    if (historicalProfileRequired) return <>
      <Form.Item name="hire_date" label="Date d'embauche applicable à la période"
        rules={[{ required: true, message: "Renseignez la date d'embauche historique" }]}>
        <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }}
          disabledDate={date => Boolean(periodEndDate && date.isAfter(dayjs(periodEndDate), 'day'))} />
      </Form.Item>
      <Form.Item name="professional_category" label="Catégorie professionnelle applicable à la période"
        rules={[{ required: true, message: "Sélectionnez la catégorie professionnelle historique" }]}>
        <Select options={[{ value: 'cadre', label: 'Cadre' }, { value: 'non_cadre', label: 'Non cadre' }]} />
      </Form.Item>
      <Form.Item name="dependents_count" label="Nombre de personnes à charge applicable à la période"
        rules={[{ required: true, message: "Renseignez le nombre de personnes à charge historique" },
          { validator: async (_, value) => { if (value != null && (!Number.isInteger(value) || value < 0)) throw new Error('Saisissez un entier supérieur ou égal à zéro'); } }]}>
        <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} />
      </Form.Item>
    </>;
    const isAttendance = [
      "attendance_days",
      "vacation_days",
      "sick_days",
      "unpaid_leave",
    ].includes(variableType);
    const isOvertime = ["overtime_hours", "overtime_rate"].includes(
      variableType
    );
    const isMonetary = [
      "salaire_base",
      "bonus",
      "allowance",
      "commission",
      "advance",
      "loan_deduction",
      "other_deduction",
    ].includes(variableType);

    if (isAttendance) {
      return (
        <Form.Item
          name="days"
          label="Nombre de jours"
          rules={[{ required: true, message: "Saisissez le nombre de jours" }]}
        >
          <InputNumber
            min={0}
            max={31}
            placeholder="Ex: 2"
            style={{ width: "100%" }}
            suffix="jours"
          />
        </Form.Item>
      );
    }

    if (isOvertime) {
      return (
        <Form.Item
          name="hours"
          label={
            variableType === "overtime_hours"
              ? "Nombre d'heures"
              : "Taux de majoration"
          }
          rules={[{ required: true, message: "Saisissez la valeur" }]}
        >
          <InputNumber
            min={0}
            step={variableType === "overtime_rate" ? 0.1 : 0.5}
            placeholder={variableType === "overtime_rate" ? "Ex: 1.5" : "Ex: 8"}
            style={{ width: "100%" }}
            suffix={variableType === "overtime_hours" ? "heures" : "×"}
          />
        </Form.Item>
      );
    }

    if (isMonetary) {
      return (
        <Form.Item
          name="amount"
          label={variableType === 'salaire_base' ? 'Montant mensuel de la période' : initialVariableType ? 'Montant de la période' : 'Montant'}
          rules={[{ required: true, message: "Saisissez le montant" }]}
        >
          <InputNumber
            min={0}
            step={1000}
            placeholder="Ex: 50000"
            style={{ width: "100%" }}
            addonAfter="FCFA"
          />
        </Form.Item>
      );
    }

    return null;
  };

  const getRelevantPayrollItems = () => {
    if (!variableType) return [];

    const isGain = ["bonus", "allowance", "commission"].includes(variableType);
    const isDeduction = [
      "advance",
      "loan_deduction",
      "other_deduction",
    ].includes(variableType);

    return payrollItems.filter((item) => {
      if (isGain) return item.type === "gain";
      if (isDeduction) return item.type === "deduction";
      return false;
    });
  };

  if (!employee) return null;

  const relevantItems = getRelevantPayrollItems();
  const showPayrollItems = [
    "bonus",
    "allowance",
    "commission",
    "advance",
    "loan_deduction",
    "other_deduction",
  ].includes(variableType);

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined />
          <Title level={4} style={{ margin: 0 }}>
            {historicalProfileRequired ? 'Profil applicable à cette période' : 'Ajouter Variable'} - {employee.first_name} {employee.last_name}
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!variableType}
        >
          {historicalProfileRequired ? 'Enregistrer le profil de la période' : 'Ajouter Variable'}
        </Button>,
      ]}
    >
      {initialVariableType && <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message={historicalProfileRequired ? `Profil historique à vérifier pour ${periodName || 'cette période'}` : `Montant applicable à ${periodName || 'cette période'}`}
        description={historicalProfileRequired ? "Renseignez et vérifiez les informations applicables à cette période. Elles déterminent l’ancienneté et les charges familiales ; leur enregistrement explicite permet la préparation de cette paie passée." : "Saisissez le montant mensuel applicable à cette période. Cette variable sera conservée dans sa préparation de paie."} />}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Employé:</Text>
            <br />
            <Text strong>
              {employee.first_name} {employee.last_name}
            </Text>
            <br />
            <Text type="secondary">{employee.employee_number}</Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">{initialVariableType ? 'Période de paie :' : 'Salaire de base:'}</Text>
            <br />
            <Text strong>{initialVariableType ? periodName : formatCurrency(employee.base_salary || 0)}</Text>
          </Col>
        </Row>
      </Card>

      <Form form={form} layout="vertical">
        <Form.Item
          name="variable_type"
          label="Type de variable"
          rules={[
            { required: true, message: "Sélectionnez un type de variable" },
          ]}
        >
          <Select
            disabled={historicalProfileRequired}
            placeholder="Choisir le type de variable"
            onChange={value => { setVariableType(value); setSelectedPayrollItem(null); setCalculatedAmount(0); form.setFieldsValue({ payroll_item_id: undefined, amount: undefined, days: undefined, hours: undefined }); }}
            style={{ width: "100%" }}
          >
            {getVariableTypeOptions().map((group) => (
              <Select.OptGroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <Space>
                      {option.icon}
                      {option.label}
                    </Space>
                  </Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
        </Form.Item>

        {/* Sélection d'élément de paie pour gains/déductions */}
        {showPayrollItems && relevantItems.length > 0 && (
          <Form.Item name="payroll_item_id" label="Élément de paie (optionnel)">
            <Select
              placeholder="Choisir un élément de paie prédéfini"
              allowClear
              onChange={handlePayrollItemSelect}
              style={{ width: "100%" }}
            >
              {relevantItems.map((item) => (
                <Option key={item.id} value={item.id}>
                  <div>
                    <Space>
                      <Tag color={item.type === "gain" ? "green" : "red"}>
                        {item.code}
                      </Tag>
                      <Text>{item.name}</Text>
                    </Space>
                    <br />
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      {item.calculation_type === "fixed"
                        ? `Montant fixe: ${formatCurrency(item.base_amount)}`
                        : `Pourcentage: ${item.percentage}%`}
                    </Text>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {selectedPayrollItem && (
          <Alert
            message="Calcul automatique"
            description={
              selectedPayrollItem.calculation_type === "fixed"
                ? `Montant fixe: ${formatCurrency(
                    selectedPayrollItem.base_amount
                  )}`
                : `${
                    selectedPayrollItem.percentage
                  }% du salaire de base = ${formatCurrency(calculatedAmount)}`
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {getInputField()}

        <Form.Item name="description" label="Description (optionnelle)">
          <TextArea
            rows={3}
            placeholder="Description ou commentaire sur cette variable..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>

      {/* Informations d'aide */}
      {variableType && (
        <Alert
          message={
            ["bonus", "allowance", "commission"].includes(variableType)
              ? "Gain ajouté au salaire"
              : ["advance", "loan_deduction", "other_deduction"].includes(
                  variableType
                )
              ? "Déduction retirée du salaire"
              : historicalProfileRequired ? "Profil conservé pour cette période de paie" : "Variable de présence/temps"
          }
          type={
            ["bonus", "allowance", "commission"].includes(variableType)
              ? "success"
              : ["advance", "loan_deduction", "other_deduction"].includes(
                  variableType
                )
              ? "warning"
              : "info"
          }
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default QuickAddVariableModal;
